import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import SkybitWordmark from '../assets/skybit-wordmark.svg'
import { useAuth, signOut } from '../lib/auth'
import { AuthModal } from '../components/auth-modal'

export const Route = createFileRoute('/_layout')({
  loader: async () => {
    // Always fetch on client side
    try {
      const response = await fetch('https://skybit-api.botnet-599.workers.dev/api/stats')
      if (response.ok) {
        const data = await response.json()
        return {
          globalStats: {
            totalMarkets: data.totalMarkets || 0,
            totalVolume: data.totalVolume || 0,
            totalLiquidity: data.totalLiquidity || 0,
            avgYesPrice: data.avgYesPrice || 0,
            avgNoPrice: data.avgNoPrice || 0,
            avgSpread: data.avgSpread || 0,
            opportunityCount: data.unique_opportunities || 0,
            platforms: data.platforms || {
              polymarket: { count: 0, volume: 0, liquidity: 0 },
              kalshi: { count: 0, volume: 0, liquidity: 0 },
              predictit: { count: 0, volume: 0, liquidity: 0 },
              limitless: { count: 0, volume: 0, liquidity: 0 }
            }
          }
        }
      }
    } catch (error) {
      // Silently fail
    }
    return {
      globalStats: {
        totalMarkets: 0,
        totalVolume: 0,
        totalLiquidity: 0,
        avgYesPrice: 0,
        avgNoPrice: 0,
        avgSpread: 0,
        opportunityCount: 0,
        platforms: {
          polymarket: { count: 0, volume: 0, liquidity: 0 },
          kalshi: { count: 0, volume: 0, liquidity: 0 },
          predictit: { count: 0, volume: 0, liquidity: 0 },
          limitless: { count: 0, volume: 0, liquidity: 0 }
        }
      }
    }
  },
  component: LayoutComponent,
})

function LayoutComponent() {
  const { globalStats } = Route.useLoaderData()
  const navigate = useNavigate()
  
  // Get auth state from Better Auth
  const { isLoggedIn, user, isLoading: authLoading } = useAuth()
  
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) return savedTheme
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })
  
  const [currentDate, setCurrentDate] = useState(new Date())
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup'>('signin')
  
  // Update date every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])
  
  // Update theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])
  
  const toggleTheme = () => {
    // Temporarily disable all transitions for instant switching
    const style = document.createElement('style')
    style.textContent = '* { transition: none !important; }'
    document.head.appendChild(style)
    
    setTheme(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark'
      
      // Re-enable transitions after a frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          style.remove()
        })
      })
      
      return newTheme
    })
  }
  
  const pathname = window.location.pathname
  
  return (
    <div className="layout-container">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <img 
            src={SkybitWordmark} 
            alt="Skybit" 
            className="logo" 
            style={{ 
              height: '24px', 
              width: 'auto',
              filter: theme === 'dark' ? 'invert(1)' : 'none' 
            }} 
          />
        </div>
        <div className="header-right">
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="8" cy="8" r="3" />
                <circle cx="8" cy="2" r="0.7" />
                <circle cx="8" cy="14" r="0.7" />
                <circle cx="14" cy="8" r="0.7" />
                <circle cx="2" cy="8" r="0.7" />
                <circle cx="12.2" cy="3.8" r="0.7" />
                <circle cx="3.8" cy="12.2" r="0.7" />
                <circle cx="12.2" cy="12.2" r="0.7" />
                <circle cx="3.8" cy="3.8" r="0.7" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z"/>
              </svg>
            )}
          </button>
          <div className="date-display">
            {currentDate.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false
            }).toUpperCase()}
          </div>
          <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {authLoading ? (
              <span style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>LOADING...</span>
            ) : isLoggedIn ? (
              <>
                <span style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
                  {user?.email?.toUpperCase() || 'USER'}
                </span>
                <button 
                  className="action-btn"
                  style={{ fontSize: '0.6rem', padding: '0.25rem 0.75rem' }}
                  onClick={async () => {
                    await signOut();
                    navigate({ to: '/' });
                  }}
                >
                  SIGN OUT
                </button>
              </>
            ) : (
              <>
                <span 
                  style={{ 
                    fontSize: '0.65rem', 
                    color: 'var(--sacred-text-dim)',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => {
                    setAuthModalMode('signin')
                    setAuthModalOpen(true)
                  }}
                >
                  SIGN IN
                </span>
                <button 
                  className="action-btn primary"
                  style={{ fontSize: '0.6rem', padding: '0.25rem 0.75rem' }}
                  onClick={() => {
                    setAuthModalMode('signup')
                    setAuthModalOpen(true)
                  }}
                >
                  SIGN UP
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="tab-nav">
        <button 
          className={`tab ${pathname === '/' || pathname === '/markets' ? 'active' : ''}`}
          onClick={() => navigate({ to: '/markets' })}
        >
          Markets
        </button>
        <button 
          className={`tab ${pathname === '/opportunities' ? 'active' : ''}`}
          onClick={() => navigate({ to: '/opportunities' })}
        >
          Opportunities
        </button>
        <button 
          className={`tab ${pathname === '/alerts' ? 'active' : ''}`}
          onClick={() => navigate({ to: '/alerts' })}
        >
          Alerts
        </button>
        {isLoggedIn && (
          <>
            <button 
              className={`tab ${pathname === '/positions' ? 'active' : ''}`}
              onClick={() => navigate({ to: '/positions' })}
            >
              Positions
            </button>
            <button 
              className={`tab ${pathname === '/settings' ? 'active' : ''}`}
              onClick={() => navigate({ to: '/settings' })}
            >
              Settings
            </button>
          </>
        )}
      </nav>

      {/* Content */}
      <div className="content">
        <Outlet context={{ globalStats }} />
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authModalMode}
      />
    </div>
  )
}